import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    Chip,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputAdornment,
    IconButton,
    Divider,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import { API_URL } from '../../api';
import { useConfirm } from '../../components/ConfirmDialog';
import HowItWorksCard from '../../components/HowItWorksCard';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const STATUS_COLOR = { draft: 'default', sent: 'info', paid: 'success' };
const fmt = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(n) || 0);
const tutorName = (t) => `${t?.firstName || ''} ${t?.surname || ''}`.trim();

export default function TutorPayoutsPage() {
    const confirm = useConfirm();
    const [tab, setTab] = useState('rates');
    const [tutors, setTutors] = useState([]); // tutor + rate rows
    const [classes, setClasses] = useState([]);
    const [years, setYears] = useState([]);
    const [terms, setTerms] = useState([]);
    const [academicYearId, setAcademicYearId] = useState('');
    const [termId, setTermId] = useState('');
    const [slips, setSlips] = useState([]);
    const [previewMap, setPreviewMap] = useState({}); // tutorId → full preview object
    const [openPreview, setOpenPreview] = useState(null); // { tutor, data } | null
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const [rateEdit, setRateEdit] = useState(null); // tutor row being edited
    const [rateDefault, setRateDefault] = useState(0);
    const [rateOverrides, setRateOverrides] = useState([]);
    const [savingRate, setSavingRate] = useState(false);

    const [coverWork, setCoverWork] = useState([]);
    const [cwDialog, setCwDialog] = useState(false);
    const [cwTutorId, setCwTutorId] = useState('');
    const [cwAmount, setCwAmount] = useState(0);
    const [cwLabel, setCwLabel] = useState('');

    const loadRates = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/admin/tutor-pay-rates`, authHeaders());
            setTutors(data || []);
        } catch {
            setError('Failed to load rates.');
        }
    };

    useEffect(() => {
        setLoading(true);
        Promise.all([
            loadRates(),
            axios.get(`${API_URL}/api/admin/tutoring-classes?status=published`, authHeaders()).then((r) => setClasses(r.data || [])),
            axios.get(`${API_URL}/api/admin/academic-years`, authHeaders()).then((r) => {
                setYears(r.data || []);
                const active = (r.data || []).find((y) => y.isActive);
                if (active) setAcademicYearId(active._id);
            }),
        ]).finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (!academicYearId) { setTerms([]); return; }
        axios
            .get(`${API_URL}/api/admin/terms?academicYearId=${academicYearId}`, authHeaders())
            .then((r) => {
                setTerms(r.data || []);
                if (!termId && r.data?.length) setTermId(r.data[0]._id);
            })
            .catch(() => setTerms([]));
    }, [academicYearId]);

    const loadSlips = async () => {
        if (!termId) { setSlips([]); return; }
        const { data } = await axios.get(`${API_URL}/api/admin/tutor-payouts?termId=${termId}`, authHeaders());
        setSlips(data || []);
    };
    useEffect(() => { loadSlips(); /* eslint-disable-next-line */ }, [termId]);

    const loadCoverWork = async () => {
        if (!termId) { setCoverWork([]); return; }
        const { data } = await axios.get(
            `${API_URL}/api/admin/tutor-payouts/cover-work?termId=${termId}`,
            authHeaders()
        );
        setCoverWork(data || []);
    };
    useEffect(() => { loadCoverWork(); /* eslint-disable-next-line */ }, [termId]);

    // Auto-populate per-tutor preview numbers whenever the term or tutors list
    // changes. Lessons-held + preview-total columns light up automatically off
    // the live register data — no button click needed.
    const [previewBusy, setPreviewBusy] = useState(false);
    const refreshPreviews = async (overrideTermId) => {
        const useTerm = overrideTermId || termId;
        if (!useTerm || !tutors.length) {
            setPreviewMap({});
            return;
        }
        setPreviewBusy(true);
        try {
            const out = {};
            for (const t of tutors) {
                try {
                    const { data } = await axios.post(
                        `${API_URL}/api/admin/tutor-payouts/preview`,
                        { tutorId: t.tutor._id, termId: useTerm },
                        authHeaders()
                    );
                    out[t.tutor._id] = data;
                } catch {
                    out[t.tutor._id] = null;
                }
            }
            setPreviewMap(out);
        } finally {
            setPreviewBusy(false);
        }
    };
    useEffect(() => {
        refreshPreviews();
        /* eslint-disable-next-line */
    }, [termId, tutors.length]);

    const previewAll = async () => {
        if (!termId) return;
        const out = {};
        for (const t of tutors) {
            try {
                const { data } = await axios.post(
                    `${API_URL}/api/admin/tutor-payouts/preview`,
                    { tutorId: t.tutor._id, termId },
                    authHeaders()
                );
                out[t.tutor._id] = data;
            } catch {
                out[t.tutor._id] = null;
            }
        }
        setPreviewMap(out);
    };

    // Fetch + open the itemised preview dialog for a single tutor.
    const viewPreview = async (row) => {
        if (!termId) return;
        try {
            const { data } = await axios.post(
                `${API_URL}/api/admin/tutor-payouts/preview`,
                { tutorId: row.tutor._id, termId },
                authHeaders()
            );
            setPreviewMap((m) => ({ ...m, [row.tutor._id]: data }));
            setOpenPreview({ tutor: row.tutor, data });
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load preview.');
        }
    };

    /* --- pay rate editing --- */
    const openRateEdit = (row) => {
        setRateEdit(row);
        setRateDefault(row.defaultPerLesson || 0);
        setRateOverrides(row.overrides || []);
    };
    const addOverride = () =>
        setRateOverrides((arr) => [...arr, { tutoringClassId: '', perLesson: 0 }]);
    const updateOverride = (idx, key, val) =>
        setRateOverrides((arr) => arr.map((o, i) => (i === idx ? { ...o, [key]: val } : o)));
    const removeOverride = (idx) =>
        setRateOverrides((arr) => arr.filter((_, i) => i !== idx));
    const saveRate = async () => {
        setSavingRate(true);
        try {
            await axios.put(
                `${API_URL}/api/admin/tutor-pay-rates/${rateEdit.tutor._id}`,
                {
                    defaultPerLesson: Number(rateDefault) || 0,
                    overrides: rateOverrides.filter((o) => o.tutoringClassId),
                },
                authHeaders()
            );
            setRateEdit(null);
            await loadRates();
            setMessage('Rate saved.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setSavingRate(false);
        }
    };

    /* --- slip actions --- */
    const generateSlip = async (tutorId) => {
        try {
            const { data } = await axios.post(
                `${API_URL}/api/admin/tutor-payouts`,
                { tutorId, termId },
                authHeaders()
            );
            await loadSlips();
            setMessage(`Slip generated (total ${fmt(data.total)}).`);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };
    const previewSlipPdf = async (id) => {
        try {
            const { data } = await axios.get(
                `${API_URL}/api/admin/tutor-payouts/${id}/preview-pdf`,
                { ...authHeaders(), responseType: 'blob' }
            );
            const blobUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
            window.open(blobUrl, '_blank', 'noopener');
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load preview.');
        }
    };

    const sendSlip = async (id) => {
        if (!(await confirm({ title: 'Send slip', message: 'Send this payment slip to the tutor by email?', confirmLabel: 'Send' }))) return;
        try {
            await axios.post(`${API_URL}/api/admin/tutor-payouts/${id}/send`, {}, authHeaders());
            await loadSlips();
            setMessage('Slip sent.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };
    const markPaid = async (id) => {
        if (!(await confirm({ title: 'Mark slip paid', message: 'The tutor will get a "you have been paid" email.', confirmLabel: 'Mark paid' }))) return;
        try {
            await axios.post(`${API_URL}/api/admin/tutor-payouts/${id}/mark-paid`, {}, authHeaders());
            await loadSlips();
            setMessage('Marked paid.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };

    /* --- cover work --- */
    const openCwDialog = () => {
        setCwTutorId('');
        setCwAmount(0);
        setCwLabel('');
        setCwDialog(true);
    };
    const saveCoverWork = async () => {
        try {
            await axios.post(
                `${API_URL}/api/admin/tutor-payouts/cover-work`,
                { tutorId: cwTutorId, termId, amount: Number(cwAmount), label: cwLabel.trim() },
                authHeaders()
            );
            setCwDialog(false);
            await loadCoverWork();
            setMessage('Cover-work payment added.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };
    const removeCoverWork = async (id) => {
        if (!(await confirm({ title: 'Delete cover-work payment', message: 'Delete this cover-work payment?', confirmLabel: 'Delete', danger: true }))) return;
        try {
            await axios.delete(`${API_URL}/api/admin/tutor-payouts/cover-work/${id}`, authHeaders());
            await loadCoverWork();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4">Tutor payouts</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 640 }}>
                        Set per-tutor lesson rates with optional per-class overrides, log ad-hoc
                        cover-work payments, then generate per-term payment slips and email them out.
                    </Typography>
                    
                </Box>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1.</strong> Cover-work is invoiced separately from the per-term slip. Approved cover shifts appear at the top; click <strong>Generate</strong> to raise the cover-work invoice for a tutor.<br />
                            <strong>2.</strong> At the end of each term, click <strong>Generate term slip</strong> to produce the standing per-term payment for each tutor (based on the classes they teach).<br />
                            <strong>3.</strong> Every generated slip shows the calculation breakdown (rate × sessions) and can be re-sent by email.<br />
                            <strong>4.</strong> Slips are stored so tutors can also see them in their portal under Payments.<br />
                        </Typography>
                    </HowItWorksCard>
            </Box>


            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Tab value="rates" label="Pay rates" />
                <Tab value="slips" label="Payout slips" />
                <Tab value="cover" label="Cover work" />
            </Tabs>

            {loading && <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>}

            {tab === 'rates' && !loading && (
                <Card>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Tutor</TableCell>
                                <TableCell>Default / lesson</TableCell>
                                <TableCell>Class overrides</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tutors.map((row) => (
                                <TableRow key={row.tutor._id}>
                                    <TableCell>{tutorName(row.tutor)}</TableCell>
                                    <TableCell>{fmt(row.defaultPerLesson)}</TableCell>
                                    <TableCell>{row.overrides?.length || 0}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small" onClick={() => openRateEdit(row)}>Edit</Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {tutors.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                            No tutors yet — add accounts with role "teacher".
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Card>
            )}

            {tab === 'slips' && !loading && (
                <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Workflow:</strong> pick a term — each row's <em>Lessons held</em> and
                        <em>{' '}Preview total</em> auto-populate from the class register
                        (lessons marked "Held? = Yes" × the tutor's rate, plus any cover-work).
                        Use <em>Refresh from registers</em> if you've just changed a register elsewhere.
                        Click <em>View</em> on a row to inspect the itemised breakdown. <em>Generate</em>
                        {' '}creates a draft slip — nothing is sent yet. The send icon emails the tutor
                        a PDF slip and locks in the cover-work items. Finally, <em>Mark paid</em> records
                        that the bank transfer has gone out and sends the "you've been paid" confirmation.
                    </Alert>
                    <Card sx={{ p: 2, mb: 3 }}>
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            <TextField
                                select size="small" label="Academic year"
                                value={academicYearId}
                                onChange={(e) => { setAcademicYearId(e.target.value); setTermId(''); }}
                                sx={{ minWidth: 200 }}
                            >
                                {years.map((y) => (
                                    <MenuItem key={y._id} value={y._id}>{y.label}</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select size="small" label="Term"
                                value={termId}
                                onChange={(e) => setTermId(e.target.value)}
                                sx={{ minWidth: 160 }}
                                disabled={!terms.length}
                            >
                                {terms.map((t) => (
                                    <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
                                ))}
                            </TextField>
                            <Button
                                onClick={() => refreshPreviews()}
                                variant="outlined"
                                disabled={!termId || previewBusy}
                                title="Re-read the class registers and update each tutor's lessons-held figure"
                            >
                                {previewBusy ? 'Refreshing…' : 'Refresh from registers'}
                            </Button>
                        </Stack>
                    </Card>

                    <Card>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Tutor</TableCell>
                                    <TableCell>Lessons held</TableCell>
                                    <TableCell>Preview total</TableCell>
                                    <TableCell>Slip status</TableCell>
                                    <TableCell>Slip total</TableCell>
                                    <TableCell>Sent</TableCell>
                                    <TableCell>Paid</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tutors.map((row) => {
                                    const slip = slips.find((s) => String(s.tutorId?._id || s.tutorId) === String(row.tutor._id));
                                    const prev = previewMap[row.tutor._id];
                                    const lessonCount = prev?.lineItems?.length;
                                    const coverCount = prev?.coverWorkItems?.length;
                                    return (
                                        <TableRow key={row.tutor._id}>
                                            <TableCell>{tutorName(row.tutor)}</TableCell>
                                            <TableCell>
                                                {prev
                                                    ? `${lessonCount} lesson${lessonCount === 1 ? '' : 's'}${coverCount ? ` + ${coverCount} cover` : ''}`
                                                    : '—'}
                                            </TableCell>
                                            <TableCell>{prev ? fmt(prev.total) : '—'}</TableCell>
                                            <TableCell>
                                                {slip ? <Chip size="small" label={slip.status} color={STATUS_COLOR[slip.status]} /> : '—'}
                                            </TableCell>
                                            <TableCell>{slip ? fmt(slip.total) : '—'}</TableCell>
                                            <TableCell>{slip?.sentAt ? new Date(slip.sentAt).toLocaleDateString('en-GB') : '—'}</TableCell>
                                            <TableCell>{slip?.paidAt ? new Date(slip.paidAt).toLocaleDateString('en-GB') : '—'}</TableCell>
                                            <TableCell align="right">
                                                <Button size="small" onClick={() => viewPreview(row)} disabled={!termId}>
                                                    View
                                                </Button>
                                                {(!slip || slip.status === 'draft') && (
                                                    <Button
                                                        size="small"
                                                        onClick={() => generateSlip(row.tutor._id)}
                                                        disabled={!termId}
                                                        title="Create / refresh a draft slip from current lessons + rate"
                                                    >
                                                        {slip ? 'Refresh' : 'Generate'}
                                                    </Button>
                                                )}
                                                {slip && (
                                                    <Button
                                                        size="small"
                                                        onClick={() => previewSlipPdf(slip._id)}
                                                        title="Open the PDF the tutor will receive — no email is sent"
                                                    >
                                                        Preview PDF
                                                    </Button>
                                                )}
                                                {slip && slip.status === 'draft' && (
                                                    <IconButton onClick={() => sendSlip(slip._id)} title="Email slip to tutor"><SendIcon /></IconButton>
                                                )}
                                                {slip && slip.status === 'sent' && (
                                                    <Button size="small" onClick={() => markPaid(slip._id)}>Mark paid</Button>
                                                )}
                                                {slip?.pdfUrl && (
                                                    <Button size="small" component="a" href={slip.pdfUrl} target="_blank" rel="noreferrer">PDF</Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Card>
                </>
            )}

            {tab === 'cover' && !loading && (
                <Card>
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                select size="small" label="Academic year"
                                value={academicYearId}
                                onChange={(e) => { setAcademicYearId(e.target.value); setTermId(''); }}
                                sx={{ minWidth: 180 }}
                            >
                                {years.map((y) => (<MenuItem key={y._id} value={y._id}>{y.label}</MenuItem>))}
                            </TextField>
                            <TextField
                                select size="small" label="Term"
                                value={termId}
                                onChange={(e) => setTermId(e.target.value)}
                                sx={{ minWidth: 160 }}
                                disabled={!terms.length}
                            >
                                {terms.map((t) => (<MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>))}
                            </TextField>
                        </Stack>
                        <Button variant="contained" onClick={openCwDialog} disabled={!termId}>
                            Add cover payment
                        </Button>
                    </Box>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Tutor</TableCell>
                                <TableCell>Label</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {coverWork.map((c) => (
                                <TableRow key={c._id}>
                                    <TableCell>{tutorName(c.tutorId)}</TableCell>
                                    <TableCell>{c.label || '—'}</TableCell>
                                    <TableCell>{fmt(c.amount)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={c.consumedByPayoutId ? 'On sent slip' : 'Pending'}
                                            color={c.consumedByPayoutId ? 'success' : 'warning'}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        {!c.consumedByPayoutId && (
                                            <IconButton onClick={() => removeCoverWork(c._id)}><DeleteIcon /></IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {coverWork.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                            No cover-work payments yet.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Card>
            )}

            {/* Rate edit dialog */}
            <Dialog open={!!rateEdit} onClose={() => setRateEdit(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{rateEdit ? `${tutorName(rateEdit.tutor)} — pay rate` : ''}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            type="number"
                            label="Default per lesson"
                            value={rateDefault}
                            onChange={(e) => setRateDefault(e.target.value)}
                            size="small"
                            InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                            inputProps={{ min: 0, step: 0.5 }}
                        />
                        <Divider />
                        <Typography variant="subtitle2">Per-class overrides</Typography>
                        {rateOverrides.map((o, idx) => (
                            <Stack direction="row" spacing={1} key={idx}>
                                <TextField
                                    select size="small" label="Class"
                                    value={o.tutoringClassId}
                                    onChange={(e) => updateOverride(idx, 'tutoringClassId', e.target.value)}
                                    sx={{ flex: 1 }}
                                >
                                    {classes.map((c) => (
                                        <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    type="number"
                                    label="Per lesson"
                                    value={o.perLesson}
                                    onChange={(e) => updateOverride(idx, 'perLesson', e.target.value)}
                                    size="small"
                                    sx={{ width: 140 }}
                                    InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                                    inputProps={{ min: 0, step: 0.5 }}
                                />
                                <IconButton onClick={() => removeOverride(idx)}><DeleteIcon /></IconButton>
                            </Stack>
                        ))}
                        <Button onClick={addOverride}>Add override</Button>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRateEdit(null)} disabled={savingRate}>Cancel</Button>
                    <Button onClick={saveRate} variant="contained" disabled={savingRate}>
                        {savingRate ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Payout preview dialog */}
            <Dialog open={!!openPreview} onClose={() => setOpenPreview(null)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {openPreview ? `${tutorName(openPreview.tutor)} — payout preview` : ''}
                </DialogTitle>
                <DialogContent dividers>
                    {openPreview && (() => {
                        const d = openPreview.data || {};
                        const lineItems = d.lineItems || [];
                        const coverItems = d.coverWorkItems || [];
                        const heldCount = lineItems.length;
                        const lessonSubtotal = d.subtotal != null
                            ? d.subtotal
                            : lineItems.reduce((sum, li) => sum + (Number(li.total) || 0), 0);
                        const coverSubtotal = coverItems.reduce((sum, ci) => sum + (Number(ci.total) || 0), 0);
                        const slip = slips.find(
                            (s) => String(s.tutorId?._id || s.tutorId) === String(openPreview.tutor._id)
                        );
                        return (
                            <Stack spacing={2}>
                                <Typography variant="body2" color="text.secondary">
                                    {heldCount} lesson{heldCount === 1 ? '' : 's'} marked as held this term
                                    {coverItems.length ? ` · ${coverItems.length} cover-work item${coverItems.length === 1 ? '' : 's'}` : ''}.
                                    Only lessons with "Held? = Yes" on the class register are paid.
                                </Typography>

                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Lessons held</Typography>
                                    {lineItems.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            No held lessons recorded for this tutor in the selected term.
                                        </Typography>
                                    ) : (
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Lesson</TableCell>
                                                    <TableCell align="right">Qty</TableCell>
                                                    <TableCell align="right">Rate</TableCell>
                                                    <TableCell align="right">Amount</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {lineItems.map((li, idx) => (
                                                    <TableRow key={li.lessonId || idx}>
                                                        <TableCell>{li.label || '—'}</TableCell>
                                                        <TableCell align="right">{li.qty ?? 1}</TableCell>
                                                        <TableCell align="right">{fmt(li.rate)}</TableCell>
                                                        <TableCell align="right">{fmt(li.total)}</TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow>
                                                    <TableCell colSpan={3} align="right"><strong>Lessons subtotal</strong></TableCell>
                                                    <TableCell align="right"><strong>{fmt(lessonSubtotal)}</strong></TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    )}
                                </Box>

                                {coverItems.length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Cover work</Typography>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Label</TableCell>
                                                    <TableCell align="right">Amount</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {coverItems.map((ci, idx) => (
                                                    <TableRow key={ci.coverWorkPaymentId || idx}>
                                                        <TableCell>{ci.label || '—'}</TableCell>
                                                        <TableCell align="right">{fmt(ci.total)}</TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow>
                                                    <TableCell align="right"><strong>Cover subtotal</strong></TableCell>
                                                    <TableCell align="right"><strong>{fmt(coverSubtotal)}</strong></TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Box>
                                )}

                                <Divider />
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="h6">Total to pay</Typography>
                                    <Typography variant="h6">{fmt(d.total)}</Typography>
                                </Stack>

                                {slip && (
                                    <Alert severity={slip.status === 'paid' ? 'success' : 'info'}>
                                        A {slip.status} slip already exists for this tutor and term ({fmt(slip.total)}).
                                        {slip.pdfUrl && (
                                            <> <a href={slip.pdfUrl} target="_blank" rel="noreferrer">View PDF</a>.</>
                                        )}
                                    </Alert>
                                )}
                            </Stack>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPreview(null)}>Close</Button>
                    {openPreview && (
                        <Button
                            variant="contained"
                            onClick={async () => {
                                await generateSlip(openPreview.tutor._id);
                                setOpenPreview(null);
                            }}
                            disabled={!termId}
                        >
                            Generate slip
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Cover work dialog */}
            <Dialog open={cwDialog} onClose={() => setCwDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add cover-work payment</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select size="small" label="Tutor"
                            value={cwTutorId}
                            onChange={(e) => setCwTutorId(e.target.value)}
                        >
                            {tutors.map((t) => (
                                <MenuItem key={t.tutor._id} value={t.tutor._id}>{tutorName(t.tutor)}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            type="number" label="Amount" value={cwAmount}
                            onChange={(e) => setCwAmount(e.target.value)} size="small"
                            InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                            inputProps={{ min: 0, step: 0.5 }}
                        />
                        <TextField
                            label="Label" value={cwLabel}
                            onChange={(e) => setCwLabel(e.target.value)} size="small"
                            placeholder="e.g. Cover · Year 11 · 28 Apr"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCwDialog(false)}>Cancel</Button>
                    <Button onClick={saveCoverWork} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}